import { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 to `target` over `duration` ms using ease-out cubic.
 * Shared by the StatCard KPI strip and the Season Wrapped story. When
 * `reducedMotion` is true (or the target is 0/non-finite) it jumps straight to
 * the final value with no animation.
 */
export function useCountUp(target: number, duration = 650, reducedMotion = false): number {
  const [value, setValue] = useState(reducedMotion ? target : 0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reducedMotion || !isFinite(target) || target === 0) {
      setValue(target);
      return;
    }
    let startTs: number | null = null;
    const step = (ts: number) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(eased * target);
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, reducedMotion]);

  return value;
}
